/**
 * Extracts user-facing error information from an Axios error.
 *
 * Supports both backend response shapes:
 *   1. Legacy controller-level:  { message: "..." }
 *   2. Error middleware / Zod:   { error: { message, details: { field: msg } } }
 *
 * @param {Error} error - Axios error (or any Error)
 * @param {string} [fallback] - Fallback message when nothing useful can be extracted
 * @returns {{ message: string, details: Record<string,string>|null }}
 */
export function getApiError(error, fallback = 'An error occurred') {
  const data = error?.response?.data;
  if (!data) return { message: error?.message || fallback, details: null };

  const details = data.error?.details || null;
  const message =
    data.message || data.error?.message || error.message || fallback;

  return { message, details };
}

/**
 * Returns the most useful single-string message from a backend error.
 *
 * If the response contains field-level `details`, they are joined into a
 * readable string (e.g. "email: Invalid email, username: Too short").
 * Otherwise the top-level message is returned.
 */
export function getApiErrorMessage(error, fallback = 'An error occurred') {
  const { message, details } = getApiError(error, fallback);

  if (details && typeof details === 'object' && !Array.isArray(details)) {
    const entries = Object.entries(details);
    if (entries.length > 0) {
      return entries.map(([, msg]) => msg).join(', ');
    }
  }

  if (Array.isArray(details) && details.length > 0) {
    return details
      .map((d) => d.message || d)
      .filter(Boolean)
      .join(', ');
  }

  return message;
}
